import { atom, selector } from 'recoil'
import { Polygon } from '@turf/helpers'
import { keyFactory } from 'src/utils/recoil'
import {
  AMDate,
  AMLocation,
  AMMobilityShoppingCenterFromLocation,
  AMMobilityShoppingCenterLocations,
} from 'src/common-services'
import { alphamapClient } from 'src/clients'

import * as mobilityAtoms from 'src/atoms/mobility'
import * as searchAtoms from 'src/atoms/search'

const SHOPPING_CENTER_NAICS = '531120'

const key = keyFactory('mobility-tenant-finder')

// Gets a possible shopping center from the current location
export const shoppingCenterFromLocation = selector<AMMobilityShoppingCenterFromLocation>({
  key: key('shopping-center-from-location'),
  get: async ({ get }) => {
    const location = get(searchAtoms.selectedLocation)
    if (!location) {
      return undefined
    }
    const response = await alphamapClient.locations.getShoppingCenterFromLocation(location.id)
    return response
  },
})

// Uses either the shopping center id location is in OR the current location if it is a shopping center
export const shoppingCenter = selector<mobilityAtoms.AMMobilityLocation | undefined>({
  key: key('shopping-center'),
  get: ({ get }) => {
    // Check if this location is a shopping center
    const location = get(searchAtoms.selectedLocation)
    if (location && location.naicsCode === SHOPPING_CENTER_NAICS) {
      return location
    }

    // Check if we have a shopping center id from the location
    const shoppingCenter = get(shoppingCenterFromLocation)
    if (shoppingCenter) {
      return shoppingCenter
    }

    return undefined
  },
})

export const shoppingCenterPolygon = selector<{ location: AMLocation; polygon: Polygon } | undefined>({
  key: key('shopping-center-polygon'),
  get: async ({ get }) => {
    const shoppingCenter = get(shoppingCenterFromLocation)
    if (!shoppingCenter) {
      return undefined
    }
    const polygon = await alphamapClient.locations.getShoppingCenterPolygon({ locationId: shoppingCenter.id })
    return {
      location: shoppingCenter,
      polygon,
    }
  },
})

const baseDate = selector<AMDate>({
  key: key('date'),
  get: async ({ get }) => {
    return get(mobilityAtoms.validEndDateString)
  },
})

export const month = atom<AMDate>({
  key: key('month'),
  default: baseDate,
})

export const tenantOverviewLocations = selector<AMMobilityShoppingCenterLocations[]>({
  key: key('tenant-overview-locations'),
  get: async ({ get }) => {
    const location = get(shoppingCenter)
    if (!location) {
      return []
    }

    const date = get(month)
    const response = await alphamapClient.mobility.getShoppingCenterLocations({
      id: location.id,
      date,
    })
    return response
  },
})

export const tenantsLocations = selector<mobilityAtoms.AMMobilityLocation[]>({
  key: key('tenants-locations'),
  get: ({ get }) => {
    const locations = get(tenantOverviewLocations)
    return Promise.all(locations.map((location) => alphamapClient.locations.getById({ id: location.id })))
  },
})

// Toggle to show only ranked tenants
export const showRankedTenantsOnly = atom<boolean>({
  key: key('show-rank-tenants-only'),
  default: false,
})

// User can hide specific locations in the nearby locations table
export const hiddenTenantsIds = atom<string[]>({
  key: key('hidden-tenants-ids'),
  default: [],
})

export const visibleTenants = selector<AMMobilityShoppingCenterLocations[]>({
  key: key('visible-tenants'),
  get: async ({ get }) => {
    const tenants = get(tenantOverviewLocations)
    const showRankedOnly = get(showRankedTenantsOnly)
    const hiddenIds = get(hiddenTenantsIds)

    return tenants.filter((tenant) => {
      if (hiddenIds.includes(tenant.id)) {
        return false
      }
      return !showRankedOnly || !!tenant.localPercentile || !!tenant.statePercentile || !!tenant.nationalPercentile
    })
  },
})

export const hiddenTenants = selector<mobilityAtoms.AMMobilityLocation[]>({
  key: key('hidden-tenants'),
  get: async ({ get }) => {
    const hiddenIds = get(hiddenTenantsIds)
    const tenants = get(tenantsLocations)
    return tenants.filter((tenant) => hiddenIds.includes(tenant.id))
  },
})

export const selectedTenant = atom<{
  id: string
  method: 'table' | 'map'
}>({
  key: key('selected-tenant'),
  default: {
    id: '',
    method: 'map',
  },
})

// If the mobility data is for the shopping center or not
// Used as a way to pick which location atom to power the mobility charts
// i.e. the location or its shopping center if it is in one
export const isShoppingCenterMobility = atom<boolean>({
  key: key('is-shopping-center-mobility'),
  default: false,
})

// Used to render the map as static image when converting the widget to an image
export const isMapStatic = atom<boolean>({
  key: key('is-map-static'),
  default: false,
})
